import { Injectable } from "@angular/core";
import { SharedService } from "./shared.service";
import { TenantsService } from "./tenants.service";

const TENANT_ACCESS_TOKEN_KEY = "tenantAccessToken";
const TENANT_TOKEN_SESSION_KEY = "tenantAccessTokenSession";
const STAFF_ACCESS_TOKEN_KEY = "accessToken";

@Injectable({ providedIn: "root" })
export class TenantImpersonationService {
  private latestRequest = 0;

  constructor(private tenantsService: TenantsService, private sharedService: SharedService) {
    // A sign-out or sign-in in another tab replaces the staff session, which ends this tab's impersonation.
    window.addEventListener("storage", (event) => {
      if (event.key === STAFF_ACCESS_TOKEN_KEY || event.key === null) this.clear();
    });
  }

  // The only writer of the tenant token: the previous tenant's token is dropped before asking for a new one,
  // so a failure never leaves another tenant's token in place. Each failure shows exactly one error.
  async impersonate(userId: string): Promise<boolean> {
    const request = ++this.latestRequest;
    const session = this.staffSession();
    this.clear();
    if (!userId) return this.showError("Could not open tenant data: no tenant user was given.");

    let token: string | undefined;
    try {
      token = (await this.tenantsService.ImpersonateUser(userId))?.AccessToken;
    } catch (e) {
      if (this.isLatest(request)) this.showError(`Could not open tenant data. ${e}`);
      return false;
    }
    // A newer impersonation (the operator switched tenants) owns the token and reports its own outcome.
    if (!this.isLatest(request)) return false;
    if (!token) return this.showError("Could not impersonate this tenant's user: no token was returned.");

    sessionStorage.setItem(TENANT_ACCESS_TOKEN_KEY, token);
    sessionStorage.setItem(TENANT_TOKEN_SESSION_KEY, session);
    return true;
  }

  // Held per browser tab, so two tabs open on two companies each keep their own tenant. It is returned only
  // under the staff session that obtained it, so no later sign-in can reuse it.
  token(): string | null {
    const session = this.staffSession();
    return session && sessionStorage.getItem(TENANT_TOKEN_SESSION_KEY) === session
      ? sessionStorage.getItem(TENANT_ACCESS_TOKEN_KEY)
      : null;
  }

  // localStorage is shared by every tab, so a tenant token found there is never kept.
  clear(): void {
    sessionStorage.removeItem(TENANT_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(TENANT_TOKEN_SESSION_KEY);
    localStorage.removeItem(TENANT_ACCESS_TOKEN_KEY);
  }

  // The signature segment identifies the staff session without storing a second usable copy of its token.
  private staffSession(): string {
    return localStorage.getItem(STAFF_ACCESS_TOKEN_KEY)?.split(".")[2] ?? "";
  }

  private isLatest(request: number): boolean {
    return request === this.latestRequest;
  }

  private showError(message: string): false {
    this.sharedService.showDialog("error", message, false);
    return false;
  }
}
